/**
 * Thai Formatting Utilities
 * Provides utilities for formatting currency, dates, and numbers in Thai format
 */

/**
 * Thai month names
 */
export const THAI_MONTHS = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
];

/**
 * Thai month names (short form)
 */
export const THAI_MONTHS_SHORT = [
  'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
];

/**
 * Thai day names
 */
export const THAI_DAYS = [
  'อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์'
];

/**
 * Thai day names (short form)
 */
export const THAI_DAYS_SHORT = [
  'อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.'
];

/**
 * Thai numerals mapping
 */
export const THAI_NUMERALS = {
  '0': '๐', '1': '๑', '2': '๒', '3': '๓', '4': '๔',
  '5': '๕', '6': '๖', '7': '๗', '8': '๘', '9': '๙'
};

/**
 * Format currency in Thai Baht format
 * @param {number} amount - Amount to format
 * @param {Object} options - Formatting options
 * @returns {string} - Formatted currency string
 */
export function formatThaiCurrency(amount, options = {}) {
  const {
    showSymbol = true,
    symbolPosition = 'suffix', // 'prefix' or 'suffix'
    decimals = 2,
    useThaiNumerals = false,
    showDecimals = true
  } = options;

  if (amount === null || amount === undefined || isNaN(amount)) {
    return showSymbol ? '0.00 บาท' : '0.00';
  }

  // Format number with commas
  const formattedAmount = showDecimals 
    ? Number(amount).toLocaleString('th-TH', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      })
    : Number(Math.round(amount)).toLocaleString('th-TH');

  // Convert to Thai numerals if requested
  const finalAmount = useThaiNumerals 
    ? convertToThaiNumerals(formattedAmount)
    : formattedAmount;

  if (!showSymbol) {
    return finalAmount;
  }

  // Add currency symbol
  return symbolPosition === 'prefix' 
    ? `฿${finalAmount}`
    : `${finalAmount} บาท`;
}

/**
 * Format date in Thai format
 * @param {Date|string|number} date - Date to format
 * @param {Object} options - Formatting options
 * @returns {string} - Formatted date string
 */
export function formatThaiDate(date, options = {}) {
  const {
    format = 'full', // 'full', 'short', 'medium', 'long'
    useBuddhistEra = true,
    useThaiNumerals = false,
    showDayName = false
  } = options;

  const dateObj = new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    return 'วันที่ไม่ถูกต้อง';
  }

  const day = dateObj.getDate();
  const month = dateObj.getMonth();
  const year = useBuddhistEra ? dateObj.getFullYear() + 543 : dateObj.getFullYear();
  const dayOfWeek = dateObj.getDay();

  let formattedDate = '';

  // Add day name if requested
  if (showDayName) {
    formattedDate += format === 'short' ? THAI_DAYS_SHORT[dayOfWeek] : THAI_DAYS[dayOfWeek];
    formattedDate += 'ที่ ';
  }

  // Format based on requested format
  switch (format) {
    case 'short':
      formattedDate += `${day}/${month + 1}/${year}`;
      break;
    case 'medium':
      formattedDate += `${day} ${THAI_MONTHS_SHORT[month]} ${year}`;
      break;
    case 'long':
      formattedDate += `${day} ${THAI_MONTHS[month]} พ.ศ. ${year}`;
      break;
    case 'full':
    default:
      formattedDate += `${day} ${THAI_MONTHS[month]} พ.ศ. ${year}`;
      break;
  }

  // Convert to Thai numerals if requested
  return useThaiNumerals ? convertToThaiNumerals(formattedDate) : formattedDate;
}

/**
 * Format time in Thai format
 * @param {Date|string|number} date - Date/time to format
 * @param {Object} options - Formatting options
 * @returns {string} - Formatted time string
 */
export function formatThaiTime(date, options = {}) {
  const {
    format = '24', // '12' or '24'
    useThaiNumerals = false,
    showSeconds = false
  } = options;

  const dateObj = new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    return 'เวลาไม่ถูกต้อง';
  }

  let hours = dateObj.getHours();
  const minutes = dateObj.getMinutes();
  const seconds = dateObj.getSeconds();
  
  let formattedTime = '';
  let period = '';

  if (format === '12') {
    period = hours >= 12 ? ' น.' : ' น.';
    hours = hours % 12;
    if (hours === 0) hours = 12;
  }

  // Format time string
  const hoursStr = hours.toString().padStart(2, '0');
  const minutesStr = minutes.toString().padStart(2, '0');
  const secondsStr = seconds.toString().padStart(2, '0');

  formattedTime = showSeconds 
    ? `${hoursStr}:${minutesStr}:${secondsStr}${period}`
    : `${hoursStr}:${minutesStr}${period}`;

  // Convert to Thai numerals if requested
  return useThaiNumerals ? convertToThaiNumerals(formattedTime) : formattedTime;
}

/**
 * Format number in Thai format
 * @param {number} number - Number to format
 * @param {Object} options - Formatting options
 * @returns {string} - Formatted number string
 */
export function formatThaiNumber(number, options = {}) {
  const {
    decimals = 0,
    useThaiNumerals = false,
    useGrouping = true
  } = options;

  if (number === null || number === undefined || isNaN(number)) {
    return useThaiNumerals ? '๐' : '0';
  }

  const formattedNumber = Number(number).toLocaleString('th-TH', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping
  });

  return useThaiNumerals ? convertToThaiNumerals(formattedNumber) : formattedNumber;
}

/**
 * Convert Arabic numerals to Thai numerals
 * @param {string} text - Text containing Arabic numerals
 * @returns {string} - Text with Thai numerals
 */
export function convertToThaiNumerals(text) {
  if (!text || typeof text !== 'string') return text;
  
  return text.replace(/[0-9]/g, (digit) => THAI_NUMERALS[digit] || digit);
}

/**
 * Convert Thai numerals to Arabic numerals
 * @param {string} text - Text containing Thai numerals
 * @returns {string} - Text with Arabic numerals
 */
export function convertToArabicNumerals(text) {
  if (!text || typeof text !== 'string') return text;
  
  const reverseMap = Object.fromEntries(
    Object.entries(THAI_NUMERALS).map(([arabic, thai]) => [thai, arabic])
  );
  
  return text.replace(/[๐-๙]/g, (digit) => reverseMap[digit] || digit);
}

/**
 * Format relative time in Thai
 * @param {Date|string|number} date - Date to compare
 * @param {Date} baseDate - Base date for comparison (default: now)
 * @returns {string} - Relative time string in Thai
 */
export function formatThaiRelativeTime(date, baseDate = new Date()) {
  const dateObj = new Date(date);
  const base = new Date(baseDate);
  
  if (isNaN(dateObj.getTime()) || isNaN(base.getTime())) {
    return 'เวลาไม่ถูกต้อง';
  }

  const diffMs = base.getTime() - dateObj.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffSeconds < 60) {
    return diffSeconds <= 0 ? 'เมื่อสักครู่' : `${diffSeconds} วินาทีที่แล้ว`;
  } else if (diffMinutes < 60) {
    return `${diffMinutes} นาทีที่แล้ว`;
  } else if (diffHours < 24) {
    return `${diffHours} ชั่วโมงที่แล้ว`;
  } else if (diffDays < 7) {
    return `${diffDays} วันที่แล้ว`;
  } else if (diffWeeks < 4) {
    return `${diffWeeks} สัปดาห์ที่แล้ว`;
  } else if (diffMonths < 12) {
    return `${diffMonths} เดือนที่แล้ว`;
  } else {
    return `${diffYears} ปีที่แล้ว`;
  }
}

/**
 * Format file size in Thai
 * @param {number} bytes - File size in bytes
 * @param {Object} options - Formatting options
 * @returns {string} - Formatted file size string
 */
export function formatThaiFileSize(bytes, options = {}) {
  const {
    useThaiNumerals = false,
    decimals = 1
  } = options;

  if (bytes === 0) return useThaiNumerals ? '๐ ไบต์' : '0 ไบต์';
  if (bytes === null || bytes === undefined || isNaN(bytes)) {
    return 'ขนาดไฟล์ไม่ถูกต้อง';
  }

  const units = ['ไบต์', 'กิโลไบต์', 'เมกะไบต์', 'กิกะไบต์', 'เทราไบต์'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = (bytes / Math.pow(k, i)).toFixed(decimals);

  const formattedSize = useThaiNumerals ? convertToThaiNumerals(size) : size;
  return `${formattedSize} ${units[i]}`;
}

/**
 * Vue composable for Thai formatting
 */
export function useThaiFormatters() {
  return {
    formatThaiCurrency,
    formatThaiDate,
    formatThaiTime,
    formatThaiNumber,
    convertToThaiNumerals,
    convertToArabicNumerals,
    formatThaiRelativeTime,
    formatThaiFileSize,
    THAI_MONTHS,
    THAI_MONTHS_SHORT,
    THAI_DAYS,
    THAI_DAYS_SHORT,
    THAI_NUMERALS
  };
}

/**
 * Vue filters for Thai formatting (for Options API)
 */
export const thaiFilters = {
  thaiCurrency: formatThaiCurrency,
  thaiDate: formatThaiDate,
  thaiTime: formatThaiTime,
  thaiNumber: formatThaiNumber,
  thaiNumerals: convertToThaiNumerals,
  thaiRelativeTime: formatThaiRelativeTime,
  thaiFileSize: formatThaiFileSize
};

export default {
  formatThaiCurrency,
  formatThaiDate,
  formatThaiTime,
  formatThaiNumber,
  convertToThaiNumerals,
  convertToArabicNumerals,
  formatThaiRelativeTime,
  formatThaiFileSize,
  useThaiFormatters,
  thaiFilters,
  THAI_MONTHS,
  THAI_MONTHS_SHORT,
  THAI_DAYS,
  THAI_DAYS_SHORT,
  THAI_NUMERALS
};